// Shared async poller for the OCC geometry ops (PTB-6193).
//
// The backend endpoints for rotate-part / align-by-face / align-face-to-x-normal /
// suggested-vendor-material / suggested-inventory-material / preview-stock /
// preview-stock-by-dimensions all now return `202 { correlation_id, status:
// "pending" }` instead of the computed result inline (the OCC computation runs
// asynchronously on the SFA's fastlane-geometry service). Callers must poll for
// the result using one of two patterns:
//
//  - rotate-part / align-by-face / align-face-to-x-normal: poll
//    `GET orders/line-items/{id}/` until `geometry_status` is "ready"/"failed".
//  - suggested-*-material / preview-stock / preview-stock-by-dimensions: poll
//    `GET orders/line-items/{id}/geometry-result/{correlation_id}/` until
//    `status` is "ready"/"failed".
//
// `pollGeometryOperation` below is the shared loop; `order-line-item.ts` wires it
// into RTK Query `queryFn`s for each of these ops so callers keep getting a single
// promise that resolves with the final result (same as the old synchronous
// endpoints did), rather than having to manage polling themselves.

import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

export const GEOMETRY_POLL_INTERVAL_MS = 1500;
export const GEOMETRY_POLL_TIMEOUT_MS = 30000;

export class GeometryOperationFailedError extends Error {
	constructor(opLabel: string) {
		super(`${opLabel} failed while computing part geometry.`);
		this.name = 'GeometryOperationFailedError';
	}
}

export class GeometryPollTimeoutError extends Error {
	constructor(opLabel: string) {
		super(`Timed out waiting for "${opLabel}" to finish.`);
		this.name = 'GeometryPollTimeoutError';
	}
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Shape of the `baseQuery` function RTK Query injects as the 4th argument of a
// `queryFn` -- a single-arg, already-bound wrapper around the endpoint's
// configured base query (see `endpointDefinitions.d.ts`'s `queryFn` type:
// `baseQuery: (arg: Parameters<BaseQuery>[0]) => ReturnType<BaseQuery>`, whose
// return type is `MaybePromise<QueryReturnValue<...>>` -- `PromiseLike`, not
// `Promise`, and not part of this package's public type exports, hence `any`
// here rather than reconstructing it). Typed against `FetchArgs` for the arg
// so callers still get argument-shape checking.
export type PollBaseQuery = (
	arg: string | FetchArgs
) => { data?: any; error?: any } | PromiseLike<{ data?: any; error?: any }>;

export interface PollGeometryOperationOptions {
	// Treat a 404 from the poll URL as "still pending" rather than a hard error.
	// Used for the geometry-result endpoint, whose row is created before the 202
	// is returned but could theoretically lag on a read replica.
	pendingOn404?: boolean;
	intervalMs?: number;
	timeoutMs?: number;
}

/**
 * Polls `url` via `baseQuery` every `intervalMs` until `getStatus(data)` returns
 * "ready" (resolves with the raw response data) or "failed" (rejects with
 * GeometryOperationFailedError). Rejects with GeometryPollTimeoutError if neither
 * happens within `timeoutMs`, or with the raw baseQuery error for any other
 * transport/HTTP failure.
 */
export async function pollGeometryOperation(
	baseQuery: PollBaseQuery,
	url: string,
	getStatus: (data: any) => string | undefined,
	opLabel: string,
	options: PollGeometryOperationOptions = {}
): Promise<any> {
	const {
		pendingOn404 = false,
		intervalMs = GEOMETRY_POLL_INTERVAL_MS,
		timeoutMs = GEOMETRY_POLL_TIMEOUT_MS,
	} = options;
	const deadline = Date.now() + timeoutMs;

	// eslint-disable-next-line no-constant-condition
	while (true) {
		const result = await baseQuery({ url, method: 'GET' });

		if (result.error) {
			const isMissingRow = pendingOn404 && (result.error as any)?.status === 404;
			if (!isMissingRow) {
				throw result.error;
			}
		} else {
			const currentStatus = getStatus(result.data);
			if (currentStatus === 'ready') {
				return result.data;
			}
			if (currentStatus === 'failed') {
				throw new GeometryOperationFailedError(opLabel);
			}
		}

		if (Date.now() >= deadline) {
			throw new GeometryPollTimeoutError(opLabel);
		}

		await sleep(intervalMs);
	}
}

/**
 * Shapes anything thrown by pollGeometryOperation (or the initial POST/GET that
 * kicks the op off) into an RTK Query-compatible `error` value so it can be
 * returned from a `queryFn` (`{ error }`) and surfaced via the usual
 * `.isError`/`.error` hook fields.
 */
export function toGeometryQueryError(err: unknown): FetchBaseQueryError {
	// Already a shaped baseQuery/FetchBaseQueryError (propagated from a failed
	// HTTP call inside the poll loop) -- pass it through as-is.
	if (err && typeof err === 'object' && !(err instanceof Error) && 'status' in (err as any)) {
		return err as FetchBaseQueryError;
	}

	const message = err instanceof Error ? err.message : 'The geometry operation failed unexpectedly.';
	return { status: 'CUSTOM_ERROR', error: message } as FetchBaseQueryError;
}

/** Extracts a human-readable message from an RTK Query error for toast display. */
export function getGeometryErrorMessage(error: unknown, fallback: string): string {
	if (!error) return fallback;
	const err = error as any;
	if (typeof err.error === 'string') return err.error;
	if (typeof err.data === 'string') return err.data;
	if (typeof err.data?.detail === 'string') return err.data.detail;
	return fallback;
}
